// noiseFilters.ts — Sebastian Lague's two terrain noise filters ported exactly
// from Procedural-Planets (SimpleNoiseFilter / RigidNoiseFilter). Both feed the
// same point through fractal octaves; Simple remaps each octave to [0,1] and sums,
// Ridged uses (1-|n|)^2 with a feedback weight for sharp ridgelines. Frequency
// starts at baseRoughness (NOT 1); no per-filter clamp — the single max(0,...) is
// global in the terrain builder.
import type { NoiseFunction3D } from './noise';

export type FilterType = 'simple' | 'ridged';

export interface NoiseLayerSettings {
  filterType: FilterType;
  /** Whether this layer contributes to elevation (layer 0 is still evaluated for masking). */
  enabled: boolean;
  /** Multiply this layer's contribution by layer-0's raw value (continent mask). */
  useFirstLayerAsMask: boolean;
  /** Octave count. */
  numLayers: number;
  /** Starting frequency (frequency initializes to baseRoughness, NOT 1). */
  baseRoughness: number;
  /** Lacunarity: frequency *= roughness each octave. */
  roughness: number;
  /** Gain: amplitude *= persistence each octave. */
  persistence: number;
  /** Overall elevation scale. */
  strength: number;
  /** Sea-level floor subtracted from the summed value (global clamp happens later). */
  minValue: number;
  /** Offset added to the scaled point each octave (Lague's `centre`). */
  centre: [number, number, number];
  /** Ridged-only: next-layer weight = clamp01(v * weightMultiplier). Ignored by Simple. */
  weightMultiplier: number;
}

export function defaultLayer(overrides: Partial<NoiseLayerSettings> = {}): NoiseLayerSettings {
  return {
    filterType: 'simple',
    enabled: true,
    useFirstLayerAsMask: false,
    numLayers: 1,
    baseRoughness: 1,
    roughness: 2,
    persistence: 0.5,
    strength: 1,
    minValue: 0,
    centre: [0, 0, 0],
    weightMultiplier: 0.8,
    ...overrides,
  };
}

export interface NoiseFilter {
  evaluate(x: number, y: number, z: number): number;
}

// SimpleNoiseFilter.Evaluate — frequency starts at baseRoughness; each octave
// remaps simplex [-1,1] -> [0,1] via (v+1)*0.5; no max(0,...) here.
export class SimpleNoiseFilter implements NoiseFilter {
  constructor(
    private readonly noise: NoiseFunction3D,
    private readonly s: NoiseLayerSettings,
  ) {}

  evaluate(x: number, y: number, z: number): number {
    const s = this.s;
    let noiseValue = 0;
    let frequency = s.baseRoughness;
    let amplitude = 1;
    for (let i = 0; i < s.numLayers; i++) {
      const v = this.noise(
        x * frequency + s.centre[0],
        y * frequency + s.centre[1],
        z * frequency + s.centre[2],
      );
      noiseValue += (v + 1) * 0.5 * amplitude;
      frequency *= s.roughness;
      amplitude *= s.persistence;
    }
    return (noiseValue - s.minValue) * s.strength;
  }
}

// RigidNoiseFilter.Evaluate — v = (1 - |noise|)^2 * weight; weight feeds FORWARD
// to the next octave. No (v+1)*0.5 remap; no max(0,...) here.
export class RidgedNoiseFilter implements NoiseFilter {
  constructor(
    private readonly noise: NoiseFunction3D,
    private readonly s: NoiseLayerSettings,
  ) {}

  evaluate(x: number, y: number, z: number): number {
    const s = this.s;
    let noiseValue = 0;
    let frequency = s.baseRoughness;
    let amplitude = 1;
    let weight = 1;
    for (let i = 0; i < s.numLayers; i++) {
      let v = 1 - Math.abs(
        this.noise(
          x * frequency + s.centre[0],
          y * frequency + s.centre[1],
          z * frequency + s.centre[2],
        ),
      );
      v *= v;
      v *= weight;
      weight = Math.min(1, Math.max(0, v * s.weightMultiplier));
      noiseValue += v * amplitude;
      frequency *= s.roughness;
      amplitude *= s.persistence;
    }
    return (noiseValue - s.minValue) * s.strength;
  }
}

export function makeFilter(noise: NoiseFunction3D, s: NoiseLayerSettings): NoiseFilter {
  return s.filterType === 'ridged'
    ? new RidgedNoiseFilter(noise, s)
    : new SimpleNoiseFilter(noise, s);
}
